/**
 * Terminal screen shown after the user finishes enrolling every requested
 * authenticator. Unlike onboarding, this flow does NOT perform a token
 * exchange and does NOT route the user into the portal — it simply confirms
 * success and invites them to close the tab.
 *
 * Uses the shared onboarding primitives so it visually matches the rest of
 * the wizard (same icon treatment, title/description typography, enrolled-
 * factor list).
 */

// TODO(cross-feature-reuse): as with the wizard, the onboarding primitives
// should be lifted into @/components/factor-enrollment/.
/* eslint-disable boundaries/element-types */
import { CheckCircleIcon } from '@newcore/icons';
import { ROUTES } from '@/common/auth-routes';
import { buildTenantPath, getTenantFromPath } from '@/common/tenant-utils';
import { Onboarding } from '@/features/onboarding/components/primitives';
import { EnrolledFactorsList } from '@/features/onboarding/components/steps/complete-step/enrolled-factors-list';
import { FactorType } from '@/features/onboarding/types/onboarding.types';
/* eslint-enable boundaries/element-types */

interface EnrollmentInvitationSuccessScreenProps {
  enrolledFactors: string[];
}

export function EnrollmentInvitationSuccessScreen({
  enrolledFactors,
}: EnrollmentInvitationSuccessScreenProps) {
  // Map transport strings back to the FactorType enum values the shared
  // ``EnrolledFactorsList`` expects. Unknown values are dropped defensively.
  const factorEnumValues = Object.values(FactorType) as string[];
  const typedFactors = enrolledFactors.filter((factor): factor is FactorType =>
    factorEnumValues.includes(factor)
  );

  // The invitation flow is unauthenticated, so we can't drop the user into
  // the portal directly — the best we can do is send them to the tenant
  // dashboard URL, which the portal's auth loader will redirect to login if
  // they aren't signed in yet.
  const tenantSlug = getTenantFromPath();
  const portalUrl = tenantSlug
    ? buildTenantPath(tenantSlug, ROUTES.DASHBOARD)
    : ROUTES.TENANT_SELECTION;

  const handleGoToPortal = () => {
    window.location.href = portalUrl;
  };

  return (
    <div
      data-testid='enrollment-invitation-success-screen'
      className='flex flex-col items-center text-center space-y-6 py-4'
    >
      <Onboarding.Icon className='h-20 w-20 bg-utility-green-100 text-utility-green-700 [&>svg]:h-10 [&>svg]:w-10'>
        <CheckCircleIcon />
      </Onboarding.Icon>

      <Onboarding.Header>
        <Onboarding.Title>You&apos;re all set</Onboarding.Title>
        <Onboarding.Description>
          The authenticator{typedFactors.length === 1 ? '' : 's'} below{' '}
          {typedFactors.length === 1 ? 'has' : 'have'} been added to your
          account.
        </Onboarding.Description>
      </Onboarding.Header>

      <EnrolledFactorsList factors={typedFactors} />

      <Onboarding.NextButton
        onClick={handleGoToPortal}
        className='w-full max-w-sm'
        data-testid='enrollment-invitation-go-to-portal'
      >
        Go to User Portal
      </Onboarding.NextButton>
    </div>
  );
}
